import React from 'react';
import { H4, P, Button, Flex, Modal, IconButton } from 'design-system-web';
import cs from 'classnames';

import styles from './ConfirmationModal.scss';
import { FONT_AWESOME_REGULAR } from 'lane-shared/helpers/constants/icons';

export function ConfirmationModal({
  isOpen,
  onClose,
  handleConfirm,
  title,
  description,
  cancelText,
  confirmText,
  testId,
  children,
  confirmActionDisabled = false,
  cancelActionDisabled = false,
  loading = false,
  /** Primary button loading spinner. When omitted, follows `loading` (backward compatible). */
  confirmLoading,
  isFullWidth = false,
  footerActionsAfterConfirm,
}: {
  isOpen: boolean;
  onClose: Function;
  handleConfirm: Function;
  title: string;
  description?: string;
  cancelText: string;
  confirmText: string;
  testId?: string;
  children?: React.ReactNode;
  confirmActionDisabled?: boolean;
  cancelActionDisabled?: boolean;
  loading?: boolean;
  confirmLoading?: boolean;
  isFullWidth?: boolean;
  /** Rendered after the primary confirm button and before cancel (e.g. tertiary actions). */
  footerActionsAfterConfirm?: React.ReactNode;
}) {
  const primaryButtonLoading =
    confirmLoading !== undefined ? confirmLoading : loading;
  const hasDescription = Boolean(description);

  return (
    <Modal
      className={
        isFullWidth ? cs(styles.modal, styles.fullWidth) : styles.modal
      }
      isOpen={isOpen}
      onClose={() => onClose()}
      title={title}
      testId={testId}
      includeHeader={false}
      isCloseButtonHidden
      disableContentPadding
      disableCloseOnBackgroundClick={loading}
    >
      <Flex
        className={styles.modalHeader}
        justify="space-between"
        align="center"
      >
        <H4>{title}</H4>
        <IconButton
          type={FONT_AWESOME_REGULAR}
          onClick={() => onClose()}
          icon="times"
          data-test="closeButton"
          size="medium"
          disabled={loading}
        />
      </Flex>

      <Flex className={styles.modalBody} direction="column" gap={4}>
        {hasDescription && <P className={styles.Description}>{description}</P>}
        {children}
      </Flex>

      <hr className={styles.divider} />

      <Flex className={styles.modalActions} gap={3} style={{ flexWrap: 'wrap' }}>
        <Button
          variant="primary"
          size="large"
          onClick={() => handleConfirm()}
          disabled={confirmActionDisabled}
          loading={primaryButtonLoading}
        >
          {confirmText}
        </Button>
        {footerActionsAfterConfirm}
        <Button
          variant="secondary"
          size="large"
          onClick={() => onClose()}
          disabled={cancelActionDisabled}
        >
          {cancelText}
        </Button>
      </Flex>
    </Modal>
  );
}
